import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    CalendarClock,
    MessageCircle,
    Phone,
    ArrowUpRight,
    Award,
    Building2,
    MapPin,
    BadgeCheck,
    Plus,
    X,
    Check,
    Loader2,
    Bell,
    Trash2,
    Pill,
    Send,
    Bookmark,
    AlertTriangle,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Money } from "@/components/ui-veda/Primitives";
import { TID } from "@/constants/testIds";

function Tile({ label, value }) {
    return (
        <div className="border border-veda-border bg-veda-surface p-4">
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                {label}
            </p>
            <p className="font-display mt-2 text-3xl text-veda-onSurface">{value}</p>
        </div>
    );
}

function DoctorProfileCard({ me }) {
    if (!me) return null;
    return (
        <section
            data-testid={TID.doctorMyProfile}
            className="border border-veda-border bg-veda-surface p-5 md:p-6"
        >
            <div className="flex items-center gap-2">
                <span className="grid h-8 w-8 place-items-center bg-veda-brand/10 text-veda-brand">
                    <BadgeCheck size={14} />
                </span>
                <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                        My credentials
                    </p>
                    <p className="font-display mt-0.5 text-lg text-veda-onSurface md:text-xl">
                        {me.title} {me.name}
                    </p>
                </div>
            </div>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
                <dl className="grid grid-cols-[110px_1fr] gap-x-4 gap-y-2 text-sm">
                    <dt className="text-veda-muted">Specialty</dt>
                    <dd className="text-veda-onSurface">{me.specialty}</dd>
                    <dt className="text-veda-muted">Degree</dt>
                    <dd className="text-veda-onSurface">
                        {me.degree_name || me.qualifications}
                    </dd>
                    <dt className="text-veda-muted">Experience</dt>
                    <dd className="text-veda-onSurface">
                        {me.experience_years} years
                    </dd>
                    <dt className="text-veda-muted">Phone</dt>
                    <dd className="text-veda-onSurface">{me.phone}</dd>
                    <dt className="text-veda-muted">Email</dt>
                    <dd className="truncate text-veda-onSurface">{me.email}</dd>
                    <dt className="text-veda-muted">License</dt>
                    <dd className="text-veda-onSurface">
                        {me.license_number || "—"}
                    </dd>
                </dl>
                <div className="flex flex-col gap-3">
                    <div className="border border-veda-border bg-veda-surface2/60 p-3">
                        <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                            <Building2 size={11} /> Clinic
                        </p>
                        <p className="mt-1 font-semibold text-veda-onSurface">
                            {me.clinic_name || "—"}
                        </p>
                        {me.clinic_address && (
                            <p className="mt-0.5 text-xs text-veda-muted">
                                {me.clinic_address}
                            </p>
                        )}
                    </div>
                    <div>
                        <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                            <MapPin size={11} /> Practice locations
                        </p>
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                            {(me.practice_locations || []).length === 0 ? (
                                <p className="text-xs text-veda-muted">
                                    None listed.
                                </p>
                            ) : (
                                me.practice_locations.map((loc) => (
                                    <span
                                        key={loc}
                                        className="rounded-pill border border-veda-border bg-veda-surface2 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-veda-onSurface"
                                    >
                                        {loc}
                                    </span>
                                ))
                            )}
                        </div>
                    </div>
                    {me.verified && (
                        <div className="inline-flex items-center gap-1.5 self-start rounded-pill bg-veda-brand/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-veda-brand">
                            <Award size={11} /> Verified by Sushen Herbs
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

function FollowUpsPanel({ items, onCreate, onToggleDone, onDelete, onNudge, patients }) {
    const [showForm, setShowForm] = useState(false);
    const [patientId, setPatientId] = useState("");
    const [date, setDate] = useState("");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);

    async function submit(e) {
        e.preventDefault();
        if (!patientId) return toast.error("Choose a patient.");
        if (!date) return toast.error("Pick a follow-up date.");
        if (!notes.trim()) return toast.error("Add a short note.");
        setSaving(true);
        try {
            await onCreate({ patient_id: patientId, scheduled_date: date, notes });
            setShowForm(false);
            setPatientId("");
            setDate("");
            setNotes("");
        } finally {
            setSaving(false);
        }
    }

    const grouped = useMemo(() => {
        const today = new Date().toISOString().slice(0, 10);
        const pending = items.filter(
            (i) => i.status !== "done" && i.status !== "cancelled",
        );
        return {
            overdue: pending.filter((i) => i.scheduled_date < today),
            today: pending.filter((i) => i.scheduled_date === today),
            upcoming: pending.filter((i) => i.scheduled_date > today),
            done: items.filter((i) => i.status === "done"),
        };
    }, [items]);

    return (
        <section
            data-testid={TID.doctorFollowUps}
            className="border border-veda-border bg-veda-surface p-5 md:p-6"
        >
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                    <span className="grid h-8 w-8 place-items-center bg-veda-brand/10 text-veda-brand">
                        <Bell size={14} />
                    </span>
                    <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                            Follow-up reminders
                        </p>
                        <p className="font-display mt-0.5 text-lg text-veda-onSurface md:text-xl">
                            {grouped.overdue.length + grouped.today.length + grouped.upcoming.length} pending
                        </p>
                    </div>
                </div>
                <button
                    onClick={() => setShowForm((s) => !s)}
                    data-testid={TID.doctorFollowUpAdd}
                    className="inline-flex items-center gap-1.5 border border-veda-brand bg-veda-brand/10 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-brand"
                >
                    {showForm ? <X size={11} /> : <Plus size={11} />}
                    {showForm ? "Close" : "Add reminder"}
                </button>
            </div>

            {showForm && (
                <form
                    onSubmit={submit}
                    data-testid={TID.doctorFollowUpForm}
                    className="mt-4 grid gap-3 border border-veda-brand/25 bg-veda-brand/[0.04] p-4 md:grid-cols-[1fr_180px]"
                >
                    <select
                        value={patientId}
                        onChange={(e) => setPatientId(e.target.value)}
                        data-testid={TID.doctorFollowUpPatient}
                        className="border border-veda-border bg-veda-surface px-3 py-2 text-sm"
                    >
                        <option value="">Choose a patient…</option>
                        {patients.map((p) => (
                            <option key={p.user_id} value={p.user_id}>
                                {p.name}
                            </option>
                        ))}
                    </select>
                    <input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        data-testid={TID.doctorFollowUpDate}
                        className="border border-veda-border bg-veda-surface px-3 py-2 text-sm"
                    />
                    <textarea
                        rows={2}
                        placeholder="What should you follow up on? (e.g. Check on Triphala results, ask about sleep)"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        data-testid={TID.doctorFollowUpNotes}
                        className="border border-veda-border bg-veda-surface px-3 py-2 text-sm md:col-span-2"
                    />
                    <button
                        type="submit"
                        disabled={saving}
                        data-testid={TID.doctorFollowUpSubmit}
                        className="md:col-span-2 md:justify-self-end inline-flex items-center gap-2 bg-veda-brand px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-white disabled:opacity-70"
                    >
                        {saving && <Loader2 size={11} className="animate-spin" />}
                        Add reminder
                    </button>
                </form>
            )}

            <div className="mt-4 flex flex-col gap-4">
                {["overdue", "today", "upcoming"].map((k) => {
                    const list = grouped[k];
                    if (!list.length) return null;
                    const label =
                        k === "overdue"
                            ? "Overdue"
                            : k === "today"
                              ? "Today"
                              : "Upcoming";
                    const color =
                        k === "overdue"
                            ? "text-veda-error"
                            : k === "today"
                              ? "text-veda-brand"
                              : "text-veda-muted";
                    return (
                        <div key={k}>
                            <p
                                className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${color}`}
                            >
                                {label} · {list.length}
                            </p>
                            <ul className="mt-2 divide-y divide-veda-divider border border-veda-border bg-veda-surface">
                                {list.map((fu) => (
                                    <li
                                        key={fu.followup_id}
                                        data-testid={TID.doctorFollowUpItem(fu.followup_id)}
                                        className="flex items-start gap-3 p-3"
                                    >
                                        <button
                                            onClick={() => onToggleDone(fu)}
                                            data-testid={TID.doctorFollowUpDone(fu.followup_id)}
                                            aria-label="Mark done"
                                            className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center border border-veda-border hover:border-veda-brand"
                                        >
                                            <Check size={11} className="opacity-0 hover:opacity-100" />
                                        </button>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-semibold text-veda-onSurface">
                                                <Link
                                                    to={`/doctor/patient/${fu.patient_id}`}
                                                    className="hover:text-veda-brand"
                                                >
                                                    {fu.patient_name}
                                                </Link>
                                                <span className="ml-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-muted">
                                                    {fu.scheduled_date}
                                                </span>
                                            </p>
                                            <p className="mt-0.5 text-xs leading-relaxed text-veda-muted">
                                                {fu.notes}
                                            </p>
                                        </div>
                                        <div className="flex flex-col items-end gap-1">
                                            <button
                                                onClick={() => onNudge(fu)}
                                                data-testid={TID.doctorFollowUpNudge(fu.followup_id)}
                                                title="Send a warm nudge to the patient"
                                                className="inline-flex items-center gap-1 rounded-pill border border-veda-brand bg-veda-brand/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-brand"
                                            >
                                                <Send size={9} /> Nudge
                                            </button>
                                            <button
                                                onClick={() => onDelete(fu.followup_id)}
                                                className="p-1 text-veda-error"
                                                aria-label="Delete"
                                            >
                                                <Trash2 size={12} />
                                            </button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    );
                })}
                {grouped.overdue.length +
                    grouped.today.length +
                    grouped.upcoming.length ===
                    0 && (
                    <p className="text-sm text-veda-muted">
                        No pending follow-ups. Add one when you'd like to check
                        back on a patient.
                    </p>
                )}
                {grouped.done.length > 0 && (
                    <details>
                        <summary className="cursor-pointer text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                            Done · {grouped.done.length}
                        </summary>
                        <ul className="mt-2 divide-y divide-veda-divider text-xs text-veda-muted">
                            {grouped.done.map((fu) => (
                                <li key={fu.followup_id} className="py-2">
                                    <span className="line-through">
                                        {fu.patient_name} · {fu.scheduled_date} ·{" "}
                                        {fu.notes}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </details>
                )}
            </div>
        </section>
    );
}

export default function DoctorDashboard() {
    const { user } = useAuth();
    const [me, setMe] = useState(null);
    const [consults, setConsults] = useState([]);
    const [followUps, setFollowUps] = useState([]);

    const loadFollowUps = useCallback(async () => {
        try {
            const { data } = await api.get("/doctor/follow-ups", {
                params: { include: "all" },
            });
            setFollowUps(data);
        } catch {
            /* noop */
        }
    }, []);

    useEffect(() => {
        api.get("/consultations").then((r) => setConsults(r.data)).catch(() => {});
        api.get("/doctor/me").then((r) => setMe(r.data)).catch(() => {});
        loadFollowUps();
    }, [loadFollowUps]);

    const revenue = consults.reduce((s, c) => s + (c.fee || 0), 0);
    const now = Date.now();
    const upcomingCount = useMemo(
        () =>
            consults.filter((c) => {
                if (c.status === "cancelled" || c.status === "completed") return false;
                const m = (c.slot || "").match(/^(\d{4}-\d{2}-\d{2})\s+(\d{1,2}):(\d{2})$/);
                if (!m) return false;
                return (
                    new Date(`${m[1]}T${m[2].padStart(2, "0")}:${m[3]}:00`).getTime() >=
                    now - 60 * 60 * 1000
                );
            }).length,
        [consults, now],
    );
    const doneCount = useMemo(
        () => consults.filter((c) => c.status === "completed").length,
        [consults],
    );

    // Unique patients from consultations — for the follow-up form
    const patients = useMemo(() => {
        const seen = new Map();
        for (const c of consults) {
            if (!seen.has(c.customer_id)) {
                seen.set(c.customer_id, {
                    user_id: c.customer_id,
                    name: c.customer_name,
                });
            }
        }
        return Array.from(seen.values());
    }, [consults]);

    async function createFollowUp(payload) {
        try {
            await api.post("/doctor/follow-ups", payload);
            toast.success("Reminder saved.");
            loadFollowUps();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Could not save.");
            throw e;
        }
    }

    async function toggleFollowUpDone(fu) {
        const nowDone = fu.status !== "done";
        try {
            await api.patch(`/doctor/follow-ups/${fu.followup_id}`, {
                status: nowDone ? "done" : "pending",
            });
            loadFollowUps();
        } catch {
            toast.error("Could not update.");
        }
    }

    async function deleteFollowUp(fid) {
        if (!window.confirm("Delete this reminder?")) return;
        try {
            await api.delete(`/doctor/follow-ups/${fid}`);
            loadFollowUps();
        } catch {
            toast.error("Could not delete.");
        }
    }

    async function nudgePatient(fu) {
        if (!window.confirm(`Send a warm check-in message to ${fu.patient_name}?`))
            return;
        try {
            const { data } = await api.post(`/doctor/follow-ups/${fu.followup_id}/nudge`);
            toast.success("Nudge sent — reminder marked done.");
            loadFollowUps();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Could not nudge.");
        }
    }

    return (
        <div data-testid={TID.doctorDashboardRoot} className="pb-16">
            <div className="border-b border-veda-border">
                <div className="veda-container py-8 md:py-12">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-veda-brand">
                        Welcome back, {me?.title || ""} {user?.name}
                    </p>
                    <h1 className="font-display mt-1 text-4xl text-veda-onSurface md:text-5xl">
                        Your practice
                    </h1>
                </div>
            </div>

            <div className="veda-container grid gap-3 pt-8 md:grid-cols-4">
                <Tile label="Consultations" value={consults.length} />
                <Tile label="Upcoming" value={upcomingCount} />
                <Tile label="Completed" value={doneCount} />
                <Tile label="Est. revenue" value={<Money value={revenue} />} />
            </div>

            <div className="veda-container mt-6 flex flex-wrap gap-2">
                <Link
                    to="/doctor/appointments"
                    data-testid={TID.doctorNavAppointments}
                    className="inline-flex items-center gap-1.5 bg-veda-inverse px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-onInverse"
                >
                    <CalendarClock size={11} /> Calendar / bookings
                    <ArrowUpRight size={11} />
                </Link>
                <Link
                    to="/doctor/chats"
                    className="inline-flex items-center gap-1.5 border border-veda-border bg-veda-surface px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-onSurface"
                >
                    <MessageCircle size={11} /> Patient chats
                </Link>
                <Link
                    to="/doctor/triage"
                    className="inline-flex items-center gap-1.5 border border-veda-error bg-veda-error/[0.06] px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-error hover:bg-veda-error hover:text-white"
                >
                    <AlertTriangle size={11} /> Urgent triage
                </Link>
            </div>

            <div className="veda-container mt-8 grid gap-6 lg:grid-cols-2">
                <DoctorProfileCard me={me} />
                <FollowUpsPanel
                    items={followUps}
                    patients={patients}
                    onCreate={createFollowUp}
                    onToggleDone={toggleFollowUpDone}
                    onDelete={deleteFollowUp}
                    onNudge={nudgePatient}
                />
            </div>

            <div className="veda-container mt-8">
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                    <p className="font-display text-2xl text-veda-onSurface md:text-3xl">
                        Recent bookings
                    </p>
                    <Link
                        to="/doctor/appointments"
                        className="text-xs font-medium text-veda-brand underline underline-offset-2"
                    >
                        See all
                    </Link>
                </div>
                {consults.length === 0 ? (
                    <p className="mt-4 text-sm text-veda-muted">
                        No consultations yet. New bookings will appear here.
                    </p>
                ) : (
                    <ul className="mt-4 divide-y divide-veda-divider">
                        {consults.slice(0, 8).map((c) => (
                            <li key={c.consultation_id}>
                                <Link
                                    to={`/consultation/${c.consultation_id}`}
                                    className="flex items-center gap-3 py-3"
                                >
                                    <span className="grid h-9 w-9 place-items-center bg-veda-surface2">
                                        {c.mode === "call" ? (
                                            <Phone size={14} />
                                        ) : (
                                            <MessageCircle size={14} />
                                        )}
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-semibold text-veda-onSurface">
                                            {c.customer_name}
                                            {(c.prescriptions || []).length > 0 && (
                                                <span className="ml-2 inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-brand">
                                                    <Pill size={9} />
                                                    {c.prescriptions.length}
                                                </span>
                                            )}
                                        </p>
                                        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                                            <CalendarClock size={10} className="mr-1 inline" />
                                            {c.slot} · {c.mode} ·{" "}
                                            <span className="capitalize">
                                                {c.status || "confirmed"}
                                            </span>
                                        </p>
                                    </div>
                                    <span className="text-sm font-semibold">
                                        <Money value={c.fee} />
                                    </span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
